<script setup lang="ts">
// Props
interface Props {
  hideAllArticles?: boolean;
  size?: 'default' | 'large';
  mode?: 'horizontal' | 'list';
  allLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hideAllArticles: true,
  size: 'large',
  mode: 'horizontal',
  allLabel: 'All Articles',
});

// Fetch all articles to get available categories
const { data: articles } = await useAsyncData('category-nav-articles', () =>
  queryCollection('articles').select('category').all()
);

// Extract unique categories from articles
const categories = computed(() => {
  if (!articles.value) return [];

  const categorySet = new Set<string>();
  articles.value.forEach((article) => {
    if (article.category) {
      categorySet.add(article.category);
    }
  });

  return Array.from(categorySet).sort();
});

// Get current route to highlight active category
const route = useRoute();
const currentCategory = computed(() => {
  if (route.path.startsWith('/articles/category/')) {
    const slug = route.params.slug as string;
    return slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // If we're on an individual article page, show the first category as active
  if (
    route.path.startsWith('/articles/') &&
    !route.path.startsWith('/articles/category/') &&
    route.path !== '/articles'
  ) {
    return categories.value[0] || null;
  }
  return null;
});

// Convert category name to URL slug
const categoryToSlug = (category: string) => {
  return category.toLowerCase().replace(/\s+/g, '-');
};
</script>

<template>
  <div v-if="categories.length > 0" class="category-nav">
    <!-- Horizontal Mode (Original) -->
    <div v-if="props.mode === 'horizontal'" class="w-full overflow-x-auto">
      <div
        class="flex items-center w-full mx-auto max-w-[1600px]"
        :class="props.size === 'large' ? 'gap-[14px]' : 'gap-4'"
      >
        <div class="flex items-center" :class="props.size === 'large' ? 'gap-[14px]' : 'gap-4'">
          <!-- All Articles link -->
          <NuxtLink
            v-if="!hideAllArticles"
            to="/articles"
            class="flex-shrink-0 font-medium rounded-full transition-colors whitespace-nowrap border bg-neutral-200 dark:bg-[#303030] border-neutral-200 dark:border-[#383838] text-neutral-600 dark:text-white hover:text-white hover:bg-[#383838] dark:hover:bg-primary-800"
            :class="[
              props.size === 'large' ? 'px-[19px] py-3 text-sm lg:text-lg' : 'px-4 py-2 text-sm',
            ]"
          >
            {{ allLabel }}
          </NuxtLink>

          <!-- Category links -->
          <NuxtLink
            v-for="category in categories"
            :key="category"
            :to="`/articles/category/${categoryToSlug(category)}`"
            class="flex-shrink-0 font-medium rounded-full transition-colors whitespace-nowrap border bg-neutral-200 dark:bg-[#303030] border-neutral-200 dark:border-[#383838] text-neutral-600 dark:text-white hover:text-white hover:bg-[#383838] dark:hover:bg-primary-800"
            :class="[
              props.size === 'large' ? 'px-[19px] py-3 text-sm lg:text-lg' : 'px-4 py-2 text-sm',
              currentCategory === category ? '!text-white bg-primary-700 dark:bg-primary-700' : '',
            ]"
          >
            {{ category }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- List Mode (New) -->
    <div v-else class="flex flex-col gap-1">
      <!-- All Articles link -->
      <NuxtLink
        v-if="!hideAllArticlesBoolean"
        to="/articles"
        class="underline"
        :class="[!currentCategory ? '' : '']"
      >
        {{ allLabel }}
      </NuxtLink>

      <!-- Category links -->
      <NuxtLink
        v-for="category in categories"
        :key="category"
        :to="`/articles/category/${categoryToSlug(category)}`"
        class="underline"
        :class="[currentCategory === category ? '' : '']"
      >
        {{ category }}
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for horizontal scroll - hidden by default, shown on hover */
.category-nav ::-webkit-scrollbar {
  height: 4px;
}

.category-nav ::-webkit-scrollbar-track {
  background-color: transparent;
  transition: background-color 0.2s ease;
}

.category-nav:hover ::-webkit-scrollbar-track {
  background-color: rgb(243 244 246); /* gray-100 */
}

@media (prefers-color-scheme: dark) {
  .category-nav:hover ::-webkit-scrollbar-track {
    background-color: rgb(31 41 55); /* gray-800 */
  }
}

.category-nav ::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 9999px;
  transition: background-color 0.2s ease;
}

.category-nav:hover ::-webkit-scrollbar-thumb {
  background-color: rgb(209 213 219); /* gray-300 */
}

@media (prefers-color-scheme: dark) {
  .category-nav:hover ::-webkit-scrollbar-thumb {
    background-color: rgb(75 85 99); /* gray-600 */
  }
}

.category-nav ::-webkit-scrollbar-thumb:hover {
  background-color: rgb(156 163 175); /* gray-400 */
}

@media (prefers-color-scheme: dark) {
  .category-nav ::-webkit-scrollbar-thumb:hover {
    background-color: rgb(107 114 128); /* gray-500 */
  }
}
</style>
